import React, {useCallback, useState} from 'react';
import {
  Row,
  Col,
  Card,
  Typography,
  Input,
  Space,
  Button,
  Statistic,
  Tree,
  Divider,
  Badge,
  Avatar,
  Empty,
  Skeleton,
  Tag,
  Tooltip,
  Popover,
} from 'antd';
import {
  SearchOutlined,
  FolderOutlined,
  DatabaseOutlined,
  StarOutlined,
  DownloadOutlined,
  CheckSquareOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import {Link} from 'react-router-dom';
import {debounce} from 'lodash';
import {useQuery} from '@tanstack/react-query';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {TEVService} from '../tevapi/io';

const {Title, Text, Paragraph} = Typography;

// Transform collections data for tree display
const transformCollectionsTree = (json) => {
  return json.map(row => ({
    title: <Link to={`collections/${row.pk}/`}>{row.name}</Link>,
    key: row.pk,
    children: transformCollectionsTree(row.children),
  }));
};

// Featured collection card component
const FeaturedCollectionCard: React.FC<{collection: any}> = ({collection}) => {
  const truncatedDescription = collection.description && collection.description.length > 100
    ? `${collection.description.substring(0, 100)}...`
    : collection.description;

  const hasLongDescription = collection.description && collection.description.length > 100;

  const fullDescriptionContent = (
    <div style={{maxWidth: '300px'}}>
      <Text strong style={{display: 'block', marginBottom: '8px'}}>
        {collection.name}
      </Text>
      <Text>{collection.description}</Text>
      {collection.extended_description && (
        <div style={{marginTop: '8px'}}>
          <Text type="secondary" style={{fontSize: '12px'}}>
            {collection.extended_description}
          </Text>
        </div>
      )}
    </div>
  );

  return (
    <Card
      hoverable
      size="small"
      style={{height: '100%'}}
      bodyStyle={{padding: '16px'}}
      actions={[
        <Link to={`/collections/${collection.pk}/`} key="view">
          <Button type="text" size="small">
            View Collection
          </Button>
        </Link>,
        <Tooltip title={`Download ${collection.name} as CSV`} key="download">
          <Button
            type="text"
            size="small"
            href={`/api/collections/${collection.pk}/download/`}
            target="_blank"
            icon={<DownloadOutlined />}
          >
            CSV
          </Button>
        </Tooltip>,
      ]}
    >
      <Card.Meta
        avatar={
          <Avatar
            icon={<FolderOutlined />}
            style={{backgroundColor: '#52c41a'}}
          />
        }
        title={
          <Space>
            <Text strong>{collection.name}</Text>
            <Badge count="Featured" style={{backgroundColor: '#faad14'}} />
          </Space>
        }
        description={
          <Space direction="vertical" size={4} style={{width: '100%'}}>
            {collection.description && (
              hasLongDescription ? (
                <Popover
                  content={fullDescriptionContent}
                  title="Full Description"
                  trigger="hover"
                  placement="topLeft"
                >
                  <Text
                    type="secondary"
                    style={{fontSize: '12px', cursor: 'help'}}
                    className="description-hover"
                  >
                    {truncatedDescription}
                  </Text>
                </Popover>
              ) : (
                <Text type="secondary" style={{fontSize: '12px'}}>
                  {collection.description}
                </Text>
              )
            )}
            <Space size="small" wrap>
              <Tag icon={<DatabaseOutlined />} color="blue">
                Items: {collection.item_count || 'N/A'}
              </Tag>
              <Tag color="green">
                Updated: {new Date(collection.edited_on).toLocaleDateString()}
              </Tag>
            </Space>
          </Space>
        }
      />
    </Card>
  );
};

// In-progress vetting card component
const InProgressVettingCard: React.FC<{vetting: any}> = ({vetting}) => {
  const progressPercent = Math.round((vetting.progress.n_vetted_items / vetting.progress.n_total_items) * 100);

  return (
    <Card
      hoverable
      size="small"
      style={{height: '100%'}}
      bodyStyle={{padding: '16px'}}
      actions={[
        <Link to={`/vettings/${vetting.id}/`} key="continue">
          <Button type="text" size="small">
            Continue Vetting
          </Button>
        </Link>,
        <Link to={`/collections/${vetting.collection_id}/`} key="view">
          <Button type="text" size="small">
            View Collection
          </Button>
        </Link>,
      ]}
    >
      <Card.Meta
        avatar={
          <Avatar
            icon={<ClockCircleOutlined />}
            style={{backgroundColor: '#722ed1'}}
          />
        }
        title={
          <Space>
            <Text strong>{vetting.collection_name}</Text>
            <Badge count="In Progress" style={{backgroundColor: '#722ed1'}} />
          </Space>
        }
        description={
          <Space direction="vertical" size={4} style={{width: '100%'}}>
            <Text type="secondary" style={{fontSize: '12px'}}>
              Process: {vetting.process_name}
            </Text>
            <Space size="small" wrap>
              <Tag icon={<CheckSquareOutlined />} color="green">
                Progress: {vetting.progress.n_vetted_items}/{vetting.progress.n_total_items} ({progressPercent}%)
              </Tag>
              <Tag icon={<DatabaseOutlined />} color="blue">
                Your Vets: {vetting.progress.n_vets_by_user}
              </Tag>
            </Space>
            {vetting.latest_vet_time && (
              <Text type="secondary" style={{fontSize: '11px'}}>
                Latest vet: {new Date(vetting.latest_vet_time).toLocaleDateString()}
              </Text>
            )}
          </Space>
        }
      />
    </Card>
  );
};

// Hero section component
const HeroSection: React.FC<{onSearch: (query: string) => void}> = ({onSearch}) => {
  const [searchValue, setSearchValue] = useState('');

  const handleSearch = () => {
    if (searchValue.trim()) {
      onSearch(searchValue.trim());
    }
  };

  return (
    <div
      style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '60px 20px',
        borderRadius: '12px',
        marginBottom: '32px',
        color: 'white',
        textAlign: 'center',
      }}
    >
      <Space direction="vertical" size="large" style={{width: '100%'}}>
        <Space direction="vertical" size="small">
          <Title level={1} style={{color: 'white', margin: 0}}>
            <FontAwesomeIcon icon="fa-solid fa-telescope" style={{marginRight: '12px'}} />
            TEV3 - TESS Exoplanet Vetter
          </Title>
          <Title level={4} style={{color: 'rgba(255,255,255,0.9)', fontWeight: 'normal', margin: 0}}>
            Analyze and validate exoplanet candidates from TESS mission data
          </Title>
        </Space>

        <div style={{maxWidth: '600px', margin: '0 auto', width: '100%'}}>
          <Input.Search
            size="large"
            placeholder="Search for TIC IDs, TOI IDs, or collections"
            prefix={<SearchOutlined />}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onSearch={handleSearch}
            enterButton={
              <Button type="primary" size="large">
                Search
              </Button>
            }
            style={{borderRadius: '8px'}}
          />
        </div>
      </Space>
    </div>
  );
};

// Quick stats component
const QuickStats: React.FC = () => {
  const {data: stats, isLoading} = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: async () => {
      const response = await TEVService.get('/collections/dashboard_stats/');
      return response.data;
    },
    refetchOnWindowFocus: false,
  });

  if (isLoading) {
    return (
      <Row gutter={16}>
        {[1, 2, 3, 4].map(i => (
          <Col xs={12} sm={6} key={i}>
            <Card>
              <Skeleton active paragraph={{rows: 1}} />
            </Card>
          </Col>
        ))}
      </Row>
    );
  }

  return (
    <Row gutter={16}>
      <Col xs={12} sm={6}>
        <Card>
          <Statistic
            title="Total Collections"
            value={stats?.totalCollections || 0}
            prefix={<FolderOutlined />}
            valueStyle={{color: '#3f8600'}}
          />
        </Card>
      </Col>
      <Col xs={12} sm={6}>
        <Card>
          <Statistic
            title="Total Items"
            value={stats?.totalItems || 0}
            prefix={<DatabaseOutlined />}
            valueStyle={{color: '#1890ff'}}
          />
        </Card>
      </Col>
      <Col xs={12} sm={6}>
        <Card>
          <Statistic
            title="Your Vets"
            value={stats?.userVets || 0}
            prefix={<CheckSquareOutlined />}
            valueStyle={{color: '#faad14'}}
          />
        </Card>
      </Col>
      <Col xs={12} sm={6}>
        <Card>
          <Statistic
            title="In Progress"
            value={stats?.inProgressVettings || 0}
            prefix={<ClockCircleOutlined />}
            valueStyle={{color: '#722ed1'}}
          />
        </Card>
      </Col>
    </Row>
  );
};

// Main modern collections component
export const ModernCollections: React.FC = () => {
  const [queryStr, setQueryStr] = useState('');

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setQueryStr(value);
    }, 500),
    []
  );

  // In-progress vettings query
  const {data: inProgressVettings, isLoading: vettingsLoading} = useQuery({
    queryKey: ['vettings', 'in-progress'],
    queryFn: async () => {
      const response = await TEVService.get('/vettings/in_progress/');
      return response.data;
    },
    refetchOnWindowFocus: false,
  });

  // Featured collections query
  const {data: featuredCollections, isLoading: featuredLoading} = useQuery({
    queryKey: ['collections', 'featured'],
    queryFn: async () => {
      const response = await TEVService.get('/collections/featured/');
      return response.data;
    },
    refetchOnWindowFocus: false,
  });

  // All collections tree query
  const {data: allCollections, isLoading: allLoading} = useQuery({
    queryKey: ['collections', queryStr],
    queryFn: async () => {
      const response = await TEVService.get('/collections', {
        params: {name: queryStr},
      });
      return transformCollectionsTree(response.data.collections);
    },
    refetchOnWindowFocus: false,
  });

  const handleTreeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSearch(e.target.value);
  };

  const handleHeroSearch = (query: string) => {
    // Navigate to search page with query
    window.location.href = `/search?q=${encodeURIComponent(query)}`;
  };

  return (
    <div style={{padding: '20px', maxWidth: '1200px', margin: '0 auto'}}>
      {/* Hero Section */}
      <HeroSection onSearch={handleHeroSearch} />

      {/* Featured Collections */}
      <Row style={{marginBottom: '32px'}}>
        <Col span={24}>
          <Space style={{marginBottom: '16px'}}>
            <StarOutlined style={{color: '#faad14', fontSize: '20px'}} />
            <Title level={3} style={{margin: 0}}>
              Featured Collections
            </Title>
          </Space>
          <Paragraph type="secondary">
            Curated datasets and important collections for exoplanet research
          </Paragraph>

          {featuredLoading ? (
            <Row gutter={[16, 16]}>
              {[1, 2, 3, 4].map(i => (
                <Col xs={24} sm={12} lg={6} key={i}>
                  <Card>
                    <Skeleton active avatar paragraph={{rows: 2}} />
                  </Card>
                </Col>
              ))}
            </Row>
          ) : featuredCollections && featuredCollections.collections.length > 0 ? (
            <Row gutter={[16, 16]}>
              {featuredCollections.collections.map((collection: any) => (
                <Col xs={24} sm={12} lg={6} key={collection.pk}>
                  <FeaturedCollectionCard collection={collection} />
                </Col>
              ))}
            </Row>
          ) : (
            <Empty description="No featured collections available" />
          )}
        </Col>
      </Row>

      <Divider />

      {/* Quick Stats */}
      <QuickStats />

      <Divider />

      {/* In-Progress Vettings */}
      <Row style={{marginBottom: '32px'}}>
        <Col span={24}>
          <Space style={{marginBottom: '16px'}}>
            <ClockCircleOutlined style={{color: '#722ed1', fontSize: '20px'}} />
            <Title level={3} style={{margin: 0}}>
              Your In-Progress Vettings
            </Title>
          </Space>
          <Paragraph type="secondary">
            Continue working on vetting processes that are still active
          </Paragraph>

          {vettingsLoading ? (
            <Row gutter={[16, 16]}>
              {[1, 2, 3, 4].map(i => (
                <Col xs={24} sm={12} lg={6} key={i}>
                  <Card>
                    <Skeleton active avatar paragraph={{rows: 2}} />
                  </Card>
                </Col>
              ))}
            </Row>
          ) : inProgressVettings && inProgressVettings.length > 0 ? (
            <Row gutter={[16, 16]}>
              {inProgressVettings.map((vetting: any) => (
                <Col xs={24} sm={12} lg={6} key={vetting.id}>
                  <InProgressVettingCard vetting={vetting} />
                </Col>
              ))}
            </Row>
          ) : (
            <Empty description="No in-progress vettings available" />
          )}
        </Col>
      </Row>

      <Divider />

      {/* All Collections Tree */}
      <Row>
        <Col span={24}>
          <Space style={{marginBottom: '16px'}}>
            <FolderOutlined style={{color: '#1890ff', fontSize: '20px'}} />
            <Title level={3} style={{margin: 0}}>
              Browse All Collections
            </Title>
          </Space>
          <Paragraph type="secondary">
            Explore the complete hierarchy of collections organized by research topics
          </Paragraph>

          <Input
            placeholder="Search Collections by Name"
            allowClear
            onChange={handleTreeSearch}
            prefix={<SearchOutlined />}
            style={{marginBottom: '16px', maxWidth: '400px'}}
          />

          {allLoading ? (
            <Skeleton active paragraph={{rows: 4}} />
          ) : (
            <Tree
              treeData={allCollections}
              showLine
              style={{
                background: '#fafafa',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid #d9d9d9',
              }}
            />
          )}
        </Col>
      </Row>
    </div>
  );
};

export default ModernCollections;
